import { all, put, select, takeLatest } from 'redux-saga/effects';

import { hasValidCache } from '~/modules/helpers';
import { ExternalRestClient } from '~/modules/requests';

import {
  getLocations,
  getLocationsFailure,
  getLocationsSuccess,
  getTravelpayoutsLocations,
  getTravelpayoutsLocationsFailure,
  getTravelpayoutsLocationsSuccess,
} from '~/actions';
import { CONFIG } from '~/config';

import { ApiResponse, RootState, TravelpayoutsLocation } from '~/types';

type TravelpayoutsLocationResponse = {
  data: Record<string, TravelpayoutsLocation | null>;
};

/**
 * Get Locations
 *
 * @param {Object} action
 *
 */
export function* getLocationsSaga({ payload }: ReturnType<typeof getLocations>) {
  try {
    const { data = [], updatedAt = 0 } = yield select(
      (s: RootState) => s.locations.locations[payload] || {},
    );
    const hasCache = hasValidCache(updatedAt);
    let items;

    if (!hasCache || !data.length) {
      ({ items =[] } = {});
    }

    yield put(
      getLocationsSuccess(items || data, {
        cached: hasCache,
        query: payload,
        updatedAt: Date.now(),
      }),
    );
  } catch (error: any) {
    yield put(getLocationsFailure(error.message, payload));
  }
}

export function* getTravelpayoutsLocationsSaga({ payload }: ReturnType<typeof getTravelpayoutsLocations>) {
  const codes = payload.map(code => code.toUpperCase());

  try {
    const cachedLocations: Record<string, TravelpayoutsLocation | null> = yield select(
      (s: RootState) => s.locations.travelpayoutsLocations,
    );
    const missingCodes = codes.filter(code => !(code in cachedLocations));

    if (!missingCodes.length) {
      return;
    }

    const response: ApiResponse<TravelpayoutsLocationResponse> = yield ExternalRestClient.get(
      `${CONFIG.env.apiBaseUrl}v2/travelpayouts/locations?codes=${missingCodes.join(',')}`,
    );

    if (response.status === 200) {
      yield put(getTravelpayoutsLocationsSuccess(response.data?.data || {}));
    } else {
      yield put(getTravelpayoutsLocationsFailure({
        codes: missingCodes,
        errorMessage: 'Failed to fetch Travelpayouts locations.',
      }));
    }
  } catch (error: any) {
    yield put(getTravelpayoutsLocationsFailure({
      codes,
      errorMessage: error?.response?.data?.error || error.message,
    }));
  }
}

/**
 * GitHub Sagas
 */
export default function* root() {
  yield all([
    takeLatest(getLocations.type, getLocationsSaga),
    takeLatest(getTravelpayoutsLocations.type, getTravelpayoutsLocationsSaga),
  ]);
}
